import Component from '@glimmer/component';
import { service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { t } from 'ember-intl';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import Text from 'tio-ui/components/text';
import { PhSparkle } from 'ember-phosphor-icons';
import { setAnnualAutoSubmitOptedOut } from 'tio-employee/utils/pslf/annual-auto-submit';
import type { AnnualAutoSubmitStatus } from 'tio-employee/utils/pslf/annual-auto-submit';
import type { PslfProfileExtension as PslfProfileModel } from 'tio-data/data/resources/pslf-profile.ext';
import type PslfDebugService from 'tio-employee/services/pslf-debug';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type { IntlService } from 'ember-intl';
import type { Store } from '@warp-drive/core';

export interface AnnualPromoCardSignature {
  Args: {
    /** The card renders nothing unless this reports `showOptInPromo`. */
    status: AnnualAutoSubmitStatus;
    /** PATCHed by the CTA — it clears `annualAutoSubmitOptedOut` on this record. */
    profile: PslfProfileModel;
  };
  Element: HTMLDivElement;
}

/** Slim opt-in card shown under the hero while automatic submission is off and the deadline is close. */
export default class AnnualPromoCard extends Component<AnnualPromoCardSignature> {
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;
  @service declare pslfDebug: PslfDebugService;
  @service declare store: Store;

  optIn = dropTask(async () => {
    if (this.pslfDebug.isOverridingAnnual) {
      this.pslfDebug.setAnnualOptedOut(this.args.profile, false);
      return;
    }

    try {
      await setAnnualAutoSubmitOptedOut(this.store, this.args.profile, false);
    } catch (error) {
      console.error(error);
      this.notifications.add(this.intl.t('pslf.reimagined.annual_auto_submit.save_error'), {
        appearance: 'error',
      });
    }
  });

  get isUrgent() {
    return this.args.status.tier === 'danger';
  }

  <template>
    {{#if @status.showOptInPromo}}
      <Card @padding="md" data-test-annual-promo-card ...attributes>
        <div class="tio-row-between flex-wrap gap-4">
          <div class="tio-row-tight min-w-0">
            <div class="app-icon-badge app-icon-badge--violet app-icon-badge--md shrink-0">
              <PhSparkle @size={{20}} />
            </div>
            <div class="tio-col-extra-tight min-w-0">
              <Text @weight="semibold" @size="sm">
                {{#if this.isUrgent}}
                  {{t "pslf.reimagined.annual_auto_submit.promo.title_urgent"}}
                {{else}}
                  {{t "pslf.reimagined.annual_auto_submit.promo.title"}}
                {{/if}}
              </Text>
              <Text @variant="muted" @size="sm">
                {{t "pslf.reimagined.annual_auto_submit.promo.description"}}
              </Text>
            </div>
          </div>
          <Button
            @intent="primary"
            @size="sm"
            @isRunning={{this.optIn.isRunning}}
            @testHook="pslf-annual-promo__opt-in"
            data-test-annual-promo-opt-in
            {{on "click" this.optIn.perform}}
          >
            {{t "pslf.reimagined.annual_auto_submit.promo.cta"}}
          </Button>
        </div>
      </Card>
    {{/if}}
  </template>
}
